/**
 * ProfilePage - Pagina Profilo Unificata
 * Il Diario di Bordo - SaaS
 * 
 * Layout responsive:
 * - Mobile: Card sequenziali (stack verticale)
 * - Desktop: Due colonne
 * 
 * Sezioni:
 * - Profilo utente
 * - Verifica Email (solo coordinatori)
 * - Password
 * - Autenticazione 2FA
 * - Dispositivi attendibili
 * - Gestione sessioni
 * - Documenti Legali (pendenti + storico firmati)
 * - Attività recente
 * 
 * @updated Febbraio 2025 - Aggiunta visualizzazione documenti firmati
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { authApi } from '../services/api'
import api from '../services/api'
import { legalApi } from '../services/legalApi'
import ViewSignedDocumentModal from '../components/legal/ViewSignedDocumentModal'
import { useTwoFactor } from '../hooks/useTwoFactor'
import { Card, Toggle } from '../components/ui'
import { useAllFeatures, FEATURE_META } from '../hooks/useFeature'

// Componenti 2FA
import TOTPSetup from '../components/security/TOTPSetup'
import EmailOTPSetup from '../components/security/EmailOTPSetup'
import PasskeyManager from '../components/security/PasskeyManager'
import BackupCodesDisplay from '../components/security/BackupCodesDisplay'

import {
  User,
  Mail,
  Phone,
  Shield,
  Save,
  Loader2,
  Key,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Send,
  Smartphone,
  Fingerprint,
  Settings,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  Lock,
  LogOut,
  Monitor,
  Tablet,
  Clock,
  Activity,
  RefreshCw,
  Trash2,
  Star,
  MapPin,
  FileText,
  ExternalLink,
  History,
  FileCheck,
  FileClock
} from 'lucide-react'

// Helper per icona dispositivo
const getDeviceIcon = (device) => {
  const name = (device?.deviceName || '').toLowerCase()
  if (name.includes('mobile') || name.includes('iphone') || name.includes('android')) {
    return Smartphone
  }
  if (name.includes('tablet') || name.includes('ipad')) {
    return Tablet
  }
  return Monitor
}

// Helper per formattare data
const formatDate = (dateString) => {
  if (!dateString) return 'N/D'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Adesso'
  if (diffMins < 60) return `${diffMins} min fa`
  if (diffHours < 24) return `${diffHours} ore fa`
  if (diffDays === 1) return 'Ieri'
  if (diffDays < 7) return `${diffDays} giorni fa`
  
  return date.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

export default function ProfilePage() {
  const { user, tenant, isCoordinatore, updateUser } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()
  
  // Stati profilo
  const [saving, setSaving] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [formData, setFormData] = useState({
    nome: user?.nome || '',
    cognome: user?.cognome || '',
    telefono: user?.telefono || ''
  })
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' })
  const [showPasswords, setShowPasswords] = useState(false)
  
  // Stati verifica email
  const [sendingVerification, setSendingVerification] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)

  // Stati 2FA
  const [status, setStatus] = useState(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [activeSetup, setActiveSetup] = useState(null)
  const [showDisableConfirm, setShowDisableConfirm] = useState(null)
  const [disablePassword, setDisablePassword] = useState('')

  // Stati sessioni
  const [sessionsData, setSessionsData] = useState(null)
  const [activities, setActivities] = useState([])
  const [activityViewMode, setActivityViewMode] = useState('self_only') // 'self_only' | 'all_users'
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [sessionError, setSessionError] = useState(null)
  const [sessionSuccess, setSessionSuccess] = useState(null)

  // Feature preferences
  const allFeatures = useAllFeatures()
  const [featureSaving, setFeatureSaving] = useState(null) // feature key in salvataggio

  // Stati documenti legali
  const [legalDocuments, setLegalDocuments] = useState({
    pending: [],
    signed: [],
    history: [],  // Storico firme invalidate (rifirme)
    loading: true
  })

  // State per modale visualizzazione documento firmato
  const [signedDocModal, setSignedDocModal] = useState({
    isOpen: false,
    acknowledgment: null,
    isHistory: false
  })

  const {
    getStatus,
    setPreferredMethod,
    disableTOTP,
    disableEmailOTP,
    isWebAuthnSupported,
    loading,
    error,
    clearError
  } = useTwoFactor()

  // Caricamento iniziale
  useEffect(() => {
    loadStatus()
    loadSessions()
    loadActivity()
    loadLegalDocuments()
  }, [])

  // === HANDLER FEATURE TOGGLE ===
  const handleFeatureToggle = async (featureKey, newValue) => {
    setFeatureSaving(featureKey)
    try {
      await authApi.updatePreferences({ [featureKey]: newValue })
      updateUser({
        uiPreferences: {
          ...user.uiPreferences,
          features: { ...user.uiPreferences?.features, [featureKey]: newValue }
        }
      })
    } catch (error) {
      showError('Errore nel salvataggio della preferenza')
    } finally {
      setFeatureSaving(null)
    }
  }

  // === HANDLERS PROFILO ===

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.put(`/users/${user.id}`, formData)
      showSuccess('Profilo aggiornato')
    } catch (error) {
      showError('Errore nel salvataggio')
    }
    setSaving(false)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (passwordData.new !== passwordData.confirm) {
      showError('Le password non corrispondono')
      return
    }
    if (passwordData.new.length < 8) {
      showError('Minimo 8 caratteri')
      return
    }
    setSaving(true)
    try {
      await authApi.changePassword(passwordData.current, passwordData.new)
      showSuccess('Password modificata')
      setPasswordData({ current: '', new: '', confirm: '' })
      setShowPasswordForm(false)
    } catch (error) {
      showError(error.response?.data?.message || 'Password attuale non corretta')
    }
    setSaving(false)
  }

  const handleResendVerification = async () => {
    if (sendingVerification || verificationSent) return
    setSendingVerification(true)
    try {
      await api.post('/onboarding/resend-verification', {
        email: tenant?.contacts?.email || user?.email
      })
      setVerificationSent(true)
      showSuccess('Email di verifica inviata!')
    } catch (error) {
      if (error.response?.data?.code === 'RATE_LIMITED') {
        showError('Attendi qualche minuto prima di riprovare')
      } else {
        showError('Errore nell\'invio dell\'email')
      }
    } finally {
      setSendingVerification(false)
    }
  }

  // === HANDLERS 2FA ===

  const loadStatus = async () => {
    setLoadingStatus(true)
    try {
      const data = await getStatus()
      setStatus(data)
    } catch (err) {
      console.error('Errore caricamento status 2FA:', err)
    } finally {
      setLoadingStatus(false)
    }
  }

  const handleSetupComplete = async () => {
    setActiveSetup(null)
    await loadStatus()
  }

  const handleDisable = async (method) => {
    try {
      clearError()
      if (method === 'totp') {
        await disableTOTP({ password: disablePassword })
      } else if (method === 'emailOtp') {
        await disableEmailOTP({ password: disablePassword })
      }
      setShowDisableConfirm(null)
      setDisablePassword('')
      await loadStatus()
      showSuccess(`${method === 'totp' ? 'App Authenticator' : 'Email OTP'} disabilitato`)
    } catch (err) {
      showError('Errore nella disabilitazione')
    }
  }

  const handlePreferredChange = async (method) => {
    try {
      await setPreferredMethod(method)
      await loadStatus()
      showSuccess('Metodo preferito aggiornato')
    } catch (err) {
      showError('Errore nel cambio metodo preferito')
    }
  }

  // === HANDLERS SESSIONI ===

  const loadSessions = async () => {
    try {
      setLoadingSessions(true)
      const response = await api.get('/auth/security/sessions')
      if (response.data.success) {
        setSessionsData(response.data.data)
      }
    } catch (err) {
      console.error('Load sessions error:', err)
    } finally {
      setLoadingSessions(false)
    }
  }

  const loadActivity = async () => {
    try {
      const response = await api.get('/auth/security/activity?limit=10')
      if (response.data.success) {
        setActivities(response.data.data.activities)
        setActivityViewMode(response.data.data.viewMode || 'self_only')
      }
    } catch (err) {
      console.error('Load activity error:', err)
    }
  }

  // === DOCUMENTI LEGALI ===
  
  const loadLegalDocuments = async () => {
    try {
      setLegalDocuments(prev => ({ ...prev, loading: true }))
      
      // Carica documenti pendenti e storico in parallelo
      const [pendingRes, historyRes] = await Promise.all([
        legalApi.getPendingDocuments().catch(() => ({ data: { data: { pending: [] } } })),
        legalApi.getMyAcknowledgments().catch(() => ({ data: { data: { acknowledgments: [], history: [] } } }))
      ])
      
      setLegalDocuments({
        pending: pendingRes.data?.data?.pending || [],
        signed: historyRes.data?.data?.acknowledgments || [],
        history: historyRes.data?.data?.history || [],  // Storico firme invalidate (rifirme)
        loading: false
      })
    } catch (err) {
      console.error('Load legal documents error:', err)
      setLegalDocuments({ pending: [], signed: [], history: [], loading: false })
    }
  }

  // Handler per aprire modale documento firmato
  const handleViewSignedDocument = (acknowledgment, isHistory = false) => {
    setSignedDocModal({
      isOpen: true,
      acknowledgment,
      isHistory
    })
  }

  // Handler per chiudere modale documento firmato
  const handleCloseSignedDocModal = () => {
    setSignedDocModal({
      isOpen: false,
      acknowledgment: null,
      isHistory: false
    })
  }

  const handleLogoutAll = async () => {
    if (!confirm('Verrai disconnesso da tutti i dispositivi, incluso questo. Continuare?')) return
    try {
      setLoggingOut(true)
      setSessionError(null)
      await api.post('/auth/security/logout-all')
      // I cookie httpOnly vengono cancellati dal backend
      setSessionSuccess('Disconnesso da tutti i dispositivi. Reindirizzamento...')
      setTimeout(() => { window.location.href = '/login' }, 1500)
    } catch (err) {
      setSessionError(err.response?.data?.message || 'Errore durante il logout')
    } finally {
      setLoggingOut(false)
    }
  }

  const handleLogoutOthers = async () => {
    if (!confirm('Tutti gli altri dispositivi verranno disconnessi. Continuare?')) return
    try {
      setLoggingOut(true)
      setSessionError(null)
      const response = await api.post('/auth/security/logout-others')
      if (response.data.success) {
        // I nuovi cookie httpOnly vengono impostati dal backend
        setSessionSuccess(`Disconnesso da ${response.data.data.revokedDevices || 0} dispositivi`)
        loadSessions()
        loadActivity()
        setTimeout(() => setSessionSuccess(null), 3000)
      }
    } catch (err) {
      setSessionError(err.response?.data?.message || 'Errore durante il logout')
    } finally {
      setLoggingOut(false)
    }
  }

  const handleRevokeDevice = async (deviceId, deviceName) => {
    if (!confirm(`Rimuovere "${deviceName}" dai dispositivi attendibili?`)) return
    try {
      // FIX: Usa l'endpoint corretto /auth/devices invece di /auth/2fa/trusted-devices
      await api.delete(`/auth/devices/${deviceId}`)
      setSessionSuccess('Dispositivo rimosso')
      loadSessions()
      setTimeout(() => setSessionSuccess(null), 3000)
    } catch (err) {
      setSessionError('Errore nella rimozione del dispositivo')
    }
  }

  // === RENDER ===

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center mb-6">
        <User className="h-7 w-7 mr-2 text-primary-600" />
        Il Mio Profilo
      </h1>

      {/* Layout Grid: 1 colonna mobile, 2 colonne desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* ═══════════════════════════════════════════════════════════
            COLONNA SINISTRA (Mobile: prima)
            ═══════════════════════════════════════════════════════════ */}
        <div className="space-y-6">
          
          {/* Card Profilo */}
          <Card>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <User className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {user?.nome} {user?.cognome}
                </h2>
                <p className="text-gray-500 flex items-center">
                  <Mail className="h-4 w-4 mr-1" />{user?.email}
                </p>
                <span className="inline-flex items-center mt-1 text-xs px-2 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
                  <Shield className="h-3 w-3 mr-1" />
                  {user?.role === 'coordinatore' ? 'Coordinatore' : user?.role === 'responsabile' ? 'Responsabile' : 'Educatore'}
                </span>
              </div>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={formData.cognome}
                    onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className="input"
                  placeholder="+39 123 456 7890"
                />
              </div>
              <div className="flex justify-end">
                <button type="submit" disabled={saving} className="btn-primary flex items-center">
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvataggio...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Salva</>
                  )}
                </button>
              </div>
            </form>
          </Card>

          {/* Card Verifica Email - Solo Coordinatori */}
          {isCoordinatore() && (
            <Card>
              <div className="flex items-center mb-4">
                <Mail className="h-5 w-5 mr-2 text-gray-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Verifica Email</h3>
              </div>

              {tenant?.emailVerified ? (
                <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-200">Email verificata</p>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      Puoi recuperare la password in caso di smarrimento.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">Email non verificata</p>
                      <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                        Verifica la tua email per poter recuperare la password.
                      </p>
                    </div>
                  </div>

                  {verificationSent ? (
                    <div className="flex items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                      <span className="text-sm text-green-700 dark:text-green-300">
                        Email inviata! Controlla la tua casella.
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={handleResendVerification}
                      disabled={sendingVerification}
                      className="btn-primary flex items-center"
                    >
                      {sendingVerification ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Invio in corso...</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" />Invia email di verifica</>
                      )}
                    </button>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Card Password */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Key className="h-5 w-5 mr-2 text-gray-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Password</h3>
              </div>
              {!showPasswordForm && (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Modifica
                </button>
              )}
            </div>

            {showPasswordForm ? (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password Attuale
                  </label>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    required
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nuova Password
                  </label>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    required
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    className="input"
                    placeholder="Minimo 8 caratteri"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Conferma Password
                  </label>
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    required
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    className="input"
                  />
                </div>
                <label className="flex items-center text-sm text-gray-500">
                  <input
                    type="checkbox"
                    checked={showPasswords}
                    onChange={(e) => setShowPasswords(e.target.checked)}
                    className="mr-2"
                  />
                  Mostra password
                </label>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false)
                      setPasswordData({ current: '', new: '', confirm: '' })
                    }}
                    className="btn-secondary"
                  >
                    Annulla
                  </button>
                  <button type="submit" disabled={saving} className="btn-primary">
                    {saving ? 'Salvataggio...' : 'Cambia Password'}
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-gray-500 text-sm">••••••••</p>
            )}
          </Card>

          {/* Card Funzionalità */}
          {Object.keys(FEATURE_META).some(key => {
            const f = allFeatures[key]
            return f && f.roleMatch
          }) && (
            <Card>
              <div className="flex items-center mb-4">
                <Settings className="h-5 w-5 mr-2 text-gray-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Le mie funzionalità</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(FEATURE_META).map(([key, meta]) => {
                  const feature = allFeatures[key]
                  if (!feature || !feature.roleMatch) return null

                  if (feature.locked) {
                    return (
                      <div key={key} className="flex items-center justify-between py-2 opacity-60">
                        <div className="flex items-center flex-1 mr-3">
                          <span className="mr-2">{meta.icon}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{meta.label}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{meta.desc}</p>
                          </div>
                        </div>
                        <div className="flex items-center text-xs text-gray-400">
                          <Lock className="h-3.5 w-3.5 mr-1" />
                          <span>{feature.lockMessage}</span>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div key={key} className="flex items-center justify-between py-2">
                      <div className="flex items-center flex-1 mr-3">
                        <span className="mr-2">{meta.icon}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{meta.label}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{meta.desc}</p>
                        </div>
                      </div>
                      <Toggle
                        enabled={feature.active}
                        onChange={(val) => handleFeatureToggle(key, val)}
                        disabled={featureSaving === key}
                      />
                    </div>
                  )
                })}
              </div>
            </Card>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            COLONNA DESTRA (Mobile: dopo)
            ═══════════════════════════════════════════════════════════ */}
        <div className="space-y-6">

          {/* Card 2FA */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-gray-400" />
                <h3 className="font-semibold text-gray-900 dark:text-white">Autenticazione 2FA</h3>
              </div>
              {status?.enabled && (
                <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  ✓ Attiva
                </span>
              )}
            </div>

            {loadingStatus ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Metodi 2FA */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
                  
                  {/* TOTP */}
                  <div className={`p-4 rounded-lg border-2 transition-colors ${
                    status?.methods?.totp?.enabled
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Smartphone className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">App Authenticator</span>
                      </div>
                      {status?.preferredMethod === 'totp' && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    
                    {status?.methods?.totp?.enabled ? (
                      <div className="space-y-2">
                        <span className="text-sm text-green-600 dark:text-green-400 flex items-center">
                          <Check className="h-4 w-4 mr-1" /> Attivo
                        </span>
                        <div className="flex gap-2">
                          {status?.preferredMethod !== 'totp' && (
                            <button
                              onClick={() => handlePreferredChange('totp')}
                              className="text-xs text-primary-600 hover:underline"
                            >
                              Imposta preferito
                            </button>
                          )}
                          <button
                            onClick={() => setShowDisableConfirm('totp')}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Disabilita
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setActiveSetup('totp')}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        Configura <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                    )}
                  </div>

                  {/* Email OTP */}
                  <div className={`p-4 rounded-lg border-2 transition-colors ${
                    status?.methods?.emailOtp?.enabled
                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Mail className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-white">Email OTP</span>
                      </div>
                      {status?.preferredMethod === 'emailOtp' && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    
                    {status?.methods?.emailOtp?.enabled ? (
                      <div className="space-y-2">
                        <span className="text-sm text-green-600 dark:text-green-400 flex items-center">
                          <Check className="h-4 w-4 mr-1" /> Attivo
                        </span>
                        <div className="flex gap-2">
                          {status?.preferredMethod !== 'emailOtp' && (
                            <button
                              onClick={() => handlePreferredChange('emailOtp')}
                              className="text-xs text-primary-600 hover:underline"
                            >
                              Imposta preferito
                            </button>
                          )}
                          <button
                            onClick={() => setShowDisableConfirm('emailOtp')}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Disabilita
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setActiveSetup('emailOtp')}
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                      >
                        Configura <ChevronRight className="h-4 w-4 ml-1" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Passkeys */}
                <div className={`p-4 rounded-lg border-2 transition-colors ${
                  status?.methods?.webauthn?.passkeys?.length > 0
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <Fingerprint className="h-5 w-5 mr-2 text-gray-600 dark:text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white">Passkey</span>
                    </div>
                    {status?.preferredMethod === 'webauthn' && (
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    )}
                  </div>

                  {isWebAuthnSupported ? (
                    <PasskeyManager
                      passkeys={status?.methods?.webauthn?.passkeys || []}
                      onUpdate={loadStatus}
                      maxPasskeys={10}
                    />
                  ) : (
                    <p className="text-sm text-gray-500">Non supportato su questo browser</p>
                  )}
                </div>

                {/* Codici Backup */}
                {status?.enabled && (
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex items-center mb-3">
                      <Key className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white">Codici di Backup</span>
                    </div>
                    <BackupCodesDisplay
                      status={status?.backupCodes}
                      onRegenerate={loadStatus}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Modal Setup TOTP */}
            {activeSetup === 'totp' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                    <h3 className="font-semibold">Configura App Authenticator</h3>
                    <button onClick={() => setActiveSetup(null)} className="text-gray-500 hover:text-gray-700">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-4">
                    <TOTPSetup onComplete={handleSetupComplete} onCancel={() => setActiveSetup(null)} />
                  </div>
                </div>
              </div>
            )}

            {/* Modal Setup Email OTP */}
            {activeSetup === 'emailOtp' && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                    <h3 className="font-semibold">Configura Email OTP</h3>
                    <button onClick={() => setActiveSetup(null)} className="text-gray-500 hover:text-gray-700">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="p-4">
                    <EmailOTPSetup userEmail={user?.email} onComplete={handleSetupComplete} onCancel={() => setActiveSetup(null)} />
                  </div>
                </div>
              </div>
            )}

            {/* Modal Disabilita */}
            {showDisableConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6">
                  <h3 className="font-semibold text-lg mb-4">Conferma Disabilitazione</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Inserisci la tua password per disabilitare {showDisableConfirm === 'totp' ? 'App Authenticator' : 'Email OTP'}.
                  </p>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    placeholder="Password"
                    className="input mb-4"
                  />
                  {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowDisableConfirm(null)
                        setDisablePassword('')
                        clearError()
                      }}
                      className="btn-secondary"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={() => handleDisable(showDisableConfirm)}
                      disabled={!disablePassword || loading}
                      className="btn-primary bg-red-600 hover:bg-red-700"
                    >
                      {loading ? 'Disabilitazione...' : 'Disabilita'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Card Dispositivi Attendibili - Disponibile per tutti i ruoli */}
          {sessionsData && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Monitor className="h-5 w-5 mr-2 text-gray-400" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Dispositivi Attendibili</h3>
                </div>
                <span className="text-sm text-gray-500">
                  {sessionsData?.trustedDevicesCount || 0} / 10
                </span>
              </div>

              {/* Info per coordinatori/responsabili */}
              {(user?.role === 'coordinatore' || user?.role === 'responsabile') && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Nota:</strong> Come {user?.role}, i dispositivi salvati ti permettono di avere il suggerimento email al login, ma dovrai comunque verificare il codice 2FA per sicurezza.
                  </p>
                </div>
              )}

              {sessionsData?.trustedDevices?.length > 0 ? (
                <div className="space-y-3">
                  {sessionsData.trustedDevices.map((device) => {
                    const DeviceIcon = getDeviceIcon(device)
                    return (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                            <DeviceIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {device.deviceName || device.name}
                              </p>
                              {/* Badge per trustLevel */}
                              {device.trustLevel === 'full' && (
                                <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded">
                                  Salta 2FA
                                </span>
                              )}
                              {device.isCurrent && (
                                <span className="px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded">
                                  Corrente
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Ultimo uso: {formatDate(device.lastUsedAt)}
                              {device.daysRemaining !== undefined && (
                                <span className="ml-2">
                                  • Scade tra {device.daysRemaining} giorn{device.daysRemaining === 1 ? 'o' : 'i'}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeDevice(device.id, device.deviceName || device.name)}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title="Rimuovi"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nessun dispositivo attendibile salvato.
                  <br />
                  <span className="text-xs">
                    Seleziona "Ricorda questo dispositivo" durante il login per aggiungerne uno.
                  </span>
                </p>
              )}
            </Card>
          )}

          {/* Card Gestione Sessioni */}
          <Card>
            <div className="flex items-center mb-4">
              <LogOut className="h-5 w-5 mr-2 text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Gestione Sessioni</h3>
            </div>

            {/* Messaggi */}
            {sessionError && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
                <AlertTriangle className="h-5 w-5" />
                <span className="text-sm">{sessionError}</span>
                <button onClick={() => setSessionError(null)} className="ml-auto">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {sessionSuccess && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">{sessionSuccess}</span>
              </div>
            )}

            <div className="space-y-3">
              {sessionsData?.currentSession?.lastLogin && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <Clock className="h-4 w-4" />
                  <span>Ultimo accesso: {formatDate(sessionsData.currentSession.lastLogin)}</span>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Disconnetti altri dispositivi
                  </p>
                  <p className="text-xs text-gray-500">Mantieni solo questa sessione</p>
                </div>
                <button
                  onClick={handleLogoutOthers}
                  disabled={loggingOut}
                  className="px-3 py-1.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded-lg hover:bg-amber-200 text-sm flex items-center gap-1.5 disabled:opacity-50"
                >
                  {loggingOut ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                  Disconnetti
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">
                    Disconnetti tutti i dispositivi
                  </p>
                  <p className="text-xs text-red-600/70 dark:text-red-400/70">Sarai disconnesso ovunque</p>
                </div>
                <button
                  onClick={handleLogoutAll}
                  disabled={loggingOut}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-1.5 disabled:opacity-50"
                >
                  {loggingOut ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                  Disconnetti tutti
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          DOCUMENTI LEGALI - Full Width
          ═══════════════════════════════════════════════════════════ */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-gray-400" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Documenti Legali</h3>
          </div>
          <button
            onClick={loadLegalDocuments}
            disabled={legalDocuments.loading}
            className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 ${legalDocuments.loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {legalDocuments.loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="space-y-6">
            
            {/* Documenti da firmare */}
            {legalDocuments.pending.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileClock className="h-4 w-4 text-amber-500" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Da firmare ({legalDocuments.pending.length})
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {legalDocuments.pending.map(doc => (
                    <div
                      key={doc._id}
                      onClick={() => navigate(`/legal/sign/${doc._id}`)}
                      className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 cursor-pointer transition-colors"
                    >
                      <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
                        <FileText className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {doc.title}
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                          {doc.typeDisplayName || doc.type} • v{doc.version}
                        </p>
                        {doc.signatureDeadline && (
                          <p className={`text-xs mt-1 ${
                            doc.isDeadlineExpired 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            <Clock className="h-3 w-3 inline mr-1" />
                            {doc.isDeadlineExpired 
                              ? 'Scaduto' 
                              : `Entro il ${new Date(doc.signatureDeadline).toLocaleDateString('it-IT')}`
                            }
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documenti firmati (storico) */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileCheck className="h-4 w-4 text-green-500" />
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Firmati ({legalDocuments.signed.length})
                </h4>
              </div>
              
              {legalDocuments.signed.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                  Nessun documento firmato
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {legalDocuments.signed.map(ack => (
                    <div
                      key={ack._id}
                      onClick={() => handleViewSignedDocument(ack, false)}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600/50 hover:border-primary-300 dark:hover:border-primary-700 cursor-pointer transition-all group"
                    >
                      <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg group-hover:bg-green-200 dark:group-hover:bg-green-700/50 transition-colors">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {ack.documentName || ack.documentType}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          v{ack.documentVersion}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Firmato il {new Date(ack.acknowledgedAt).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <Eye className="h-5 w-5 text-primary-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Storico firme (invalidate per rifirme) */}
            {legalDocuments.history && legalDocuments.history.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <History className="h-4 w-4 text-gray-400" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Storico ({legalDocuments.history.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {legalDocuments.history.map(ack => (
                    <div
                      key={ack._id}
                      onClick={() => handleViewSignedDocument(ack, true)}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/30 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer transition-all group"
                    >
                      <div className="p-1.5 bg-gray-200 dark:bg-gray-600 rounded group-hover:bg-gray-300 dark:group-hover:bg-gray-500 transition-colors">
                        <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {ack.documentName || ack.documentId?.title || ack.documentType}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          v{ack.documentVersion} • {new Date(ack.acknowledgedAt).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full whitespace-nowrap">
                        {ack.invalidatedReasonDisplay || 'Sostituito'}
                      </span>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                        <Eye className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nessun documento */}
            {legalDocuments.pending.length === 0 && legalDocuments.signed.length === 0 && legalDocuments.history.length === 0 && (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Nessun documento disponibile</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ═══════════════════════════════════════════════════════════
          ATTIVITÀ RECENTE - Full Width
          ═══════════════════════════════════════════════════════════ */}
      {activities.length > 0 && (
        <Card className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Activity className="h-5 w-5 mr-2 text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Attività Recente</h3>
              {activityViewMode === 'all_users' && (
                <span className="ml-2 text-xs px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded-full">
                  Tutto il centro
                </span>
              )}
            </div>
            <button
              onClick={() => { loadActivity(); loadSessions(); }}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            {activities.map((activity, index) => (
              <ActivityItem 
                key={activity._id || index} 
                activity={activity} 
                showUser={activityViewMode === 'all_users'}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Modale visualizzazione documento firmato */}
      <ViewSignedDocumentModal
        isOpen={signedDocModal.isOpen}
        onClose={handleCloseSignedDocModal}
        acknowledgment={signedDocModal.acknowledgment}
        isHistory={signedDocModal.isHistory}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENTE ACTIVITY ITEM
// ═══════════════════════════════════════════════════════════════════

function ActivityItem({ activity, showUser }) {
  // Colori per tipo azione
  const colorClasses = {
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    orange: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    gray: 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-700'
  };

  const textColorClasses = {
    green: 'text-green-800 dark:text-green-200',
    orange: 'text-amber-800 dark:text-amber-200',
    red: 'text-red-800 dark:text-red-200',
    blue: 'text-blue-800 dark:text-blue-200',
    gray: 'text-gray-800 dark:text-gray-200'
  };

  const subTextColorClasses = {
    green: 'text-green-600 dark:text-green-400',
    orange: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    blue: 'text-blue-600 dark:text-blue-400',
    gray: 'text-gray-500 dark:text-gray-400'
  };

  const color = activity.actionColor || 'gray';

  // Formatta data
  const formatActivityDate = (dateString) => {
    if (!dateString) return 'N/D';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} giorni fa`;
    
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]} transition-colors`}>
      <div className="flex items-start gap-3">
        {/* Icona */}
        <span className="text-2xl flex-shrink-0">{activity.actionIcon}</span>
        
        {/* Contenuto */}
        <div className="flex-1 min-w-0">
          {/* Azione */}
          <p className={`font-medium ${textColorClasses[color]}`}>
            {activity.actionLabel}
          </p>
          
          {/* Nome utente (solo per coordinatore) */}
          {showUser && activity.userName && (
            <p className={`text-sm ${subTextColorClasses[color]} flex items-center gap-1 mt-1`}>
              <User className="h-3.5 w-3.5" />
              <span>{activity.userName}</span>
              {activity.isCurrentUser && (
                <span className="text-xs px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full ml-1">
                  tu
                </span>
              )}
            </p>
          )}
          
          {/* Geolocalizzazione */}
          {activity.locationString && (
            <p className={`text-sm ${subTextColorClasses[color]} flex items-center gap-1 mt-1`}>
              <MapPin className="h-3.5 w-3.5" />
              <span>{activity.locationString}</span>
            </p>
          )}
          
          {/* Motivo fallimento (se presente) */}
          {activity.failureReason && (
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>{getFailureReasonLabel(activity.failureReason)}</span>
            </p>
          )}
          
          {/* Data/ora */}
          <p className={`text-xs ${subTextColorClasses[color]} mt-2 flex items-center gap-1`}>
            <Clock className="h-3 w-3" />
            <span>{formatActivityDate(activity.timestamp)}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// Helper per etichette motivo fallimento
function getFailureReasonLabel(reason) {
  const labels = {
    'invalid_email': 'Email non trovata',
    'invalid_password': 'Password errata',
    'account_disabled': 'Account disabilitato',
    'account_locked': 'Account bloccato',
    'tenant_deleted': 'Centro eliminato',
    'tenant_suspended': 'Centro sospeso',
    '2fa_invalid': 'Codice 2FA errato',
    '2fa_rate_limited': 'Troppi tentativi 2FA',
    'passkey_failed': 'Passkey non valida'
  };
  return labels[reason] || reason;
}
